"use client";

import { useEffect, useState } from "react";

import { useRouter, useSearchParams } from "next/navigation";

import { Table } from "react-bootstrap";

import DatasetModal from "./dataset-modal";
import ItemModal from "./item-modal";
import ModuleModal from "./module-modal";

type OverviewTableProps = {
  overviewData: {
    [studyName: string]: { value: number; label: string }[];
  };
};

type ItemProps = {
  value: number;
  label: string;
  description: string;
  item_type_title: string;
};

export default function OverviewTable({ overviewData }: OverviewTableProps) {
  const searchParams = useSearchParams();
  const router = useRouter();

  const [showDatasetModal, setShowDatasetModal] = useState(false);
  const [datasetId, setDatasetId] = useState<number>();

  const [showModuleModal, setShowModuleModal] = useState(false);
  const [moduleId, setModuleId] = useState<number>();

  const [showItemModal, setShowItemModal] = useState(false);
  const [itemId, setItemId] = useState<number>();
  const [itemIndex, setItemIndex] = useState(0);
  const [items, setItems] = useState<ItemProps[]>([]);

  useEffect(() => {
    const datasetParam = searchParams.get("dataset");

    if (datasetParam && !showDatasetModal) {
      const parsedDatasetId = parseInt(datasetParam, 10);

      if (!isNaN(parsedDatasetId)) {
        setDatasetId(parsedDatasetId);
        setShowDatasetModal(true);
      }
    } else if (!datasetParam && showDatasetModal) {
      setShowDatasetModal(false);
    }
  }, [searchParams, showDatasetModal]);

  const handleOpenDatasetModal = (datasetId: number) => {
    setDatasetId(datasetId);
    setShowDatasetModal(true);

    const params = new URLSearchParams(searchParams);
    params.set("dataset", datasetId.toString());
    router.push(`?${params.toString()}`, { scroll: false });
  };

  const handleCloseDatasetModal = () => {
    setShowDatasetModal(false);
    setDatasetId(undefined);

    const params = new URLSearchParams(searchParams);
    params.delete("dataset");
    const newSearch = params.toString();
    router.push(newSearch ? `?${newSearch}` : window.location.pathname, { scroll: false });
  };

  const handleOpenModuleModal = (moduleId: number) => {
    setModuleId(moduleId);
    setShowModuleModal(true);
  };

  const handleCloseModuleModal = () => {
    setShowModuleModal(false);
    setModuleId(undefined);
  };

  const handleOpenItemModal = (itemId: number, items: ItemProps[], currentIndex: number) => {
    setItemId(itemId);
    setItemIndex(currentIndex);
    setItems(items);
    setShowItemModal(true);
  };

  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setItemId(undefined);
    setItems([]);
  };

  return (
    <>
      <Table striped responsive>
        <thead>
          <tr className="g2-table-header-blue">
            <th className="text-start">Study Name</th>
            <th className="text-start">Data</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(overviewData).map(([studyName, items], rowIndex) => (
            <tr key={rowIndex}>
              <td>
                <span className="g2-text-blue fw-bold">{studyName}</span>
              </td>
              <td className="text-start">
                {items.length > 0 ? (
                  items.map((item, itemIndex) => (
                    <span
                      key={`${item.value}-${itemIndex}`}
                      onClick={() => item.value !== null && handleOpenDatasetModal(item.value)}
                      style={{ cursor: item.value !== null ? "pointer" : "default" }}
                    >
                      <span className="g2-link-blue">{item.label}</span>
                      {itemIndex !== items.length - 1 && <hr className="my-2 mx-auto" style={{ width: "60%" }} />}
                    </span>
                  ))
                ) : (
                  <span>-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {showDatasetModal && datasetId && (
        <DatasetModal
          showDatasetModal={showDatasetModal}
          datasetId={datasetId}
          handleCloseDatasetModal={handleCloseDatasetModal}
          handleOpenModuleModal={handleOpenModuleModal}
        />
      )}

      {showModuleModal && datasetId && moduleId && (
        <ModuleModal
          showModuleModal={showModuleModal}
          moduleId={moduleId}
          datasetId={datasetId}
          handleCloseModuleModal={handleCloseModuleModal}
          handleOpenItemModal={handleOpenItemModal}
          setDatasetModal={setShowDatasetModal}
          setModuleModal={setShowModuleModal}
        />
      )}

      {showItemModal && itemId && (
        <ItemModal
          showItemModal={showItemModal}
          itemId={itemId}
          itemIndex={itemIndex}
          setItemIndex={setItemIndex}
          items={items}
          handleCloseItemModal={handleCloseItemModal}
          setItemModal={setShowItemModal}
          setModuleModal={setShowModuleModal}
          setDatasetModal={setShowDatasetModal}
          setItemId={setItemId}
        />
      )}
    </>
  );
}
